<template>
  <div ref="root" class="v5-pop-wrap">
    <!-- Matchmaking players get the profile sidebar (party and friends) instead of the dropdown -->
    <button
      id="v5-me"
      class="v5-ib v5-me"
      type="button"
      :aria-label="t('v5.shell.account_menu', { name })"
      :aria-expanded="String(sidebar ? panelOpen : open)"
      :aria-controls="sidebar ? 'v5-profile' : 'v5-account'"
      @click="sidebar ? emit('panel') : toggle()"
    >
      <img class="v5-avatar" :class="{ 'is-imp': impersonating }" :src="avatar" alt="">
      <span class="v5-me__name">{{ name }}</span><ChevronDownIcon class="i" aria-hidden="true" />
    </button>
    <div v-if="open && !sidebar" id="v5-account" class="v5-pop v5-pop--menu">
      <div class="v5-who"><small>{{ t('v5.shell.signed_in_as') }}</small><b>{{ name }}</b></div>
      <router-link v-if="user.is_beta_tester" class="v5-who__beta" to="/settings#beta-access">
        <StarIcon class="i" aria-hidden="true" />{{ t('v5.shell.beta_access') }}
      </router-link>
      <router-link class="v5-nav" :to="profileLink"><UserCircleIcon class="i" aria-hidden="true" />{{ t('v5.shell.profile') }}</router-link>
      <router-link v-if="user.access_level > 2" class="v5-nav" :to="teamLink"><UserGroupIcon class="i" aria-hidden="true" />{{ t('v5.shell.team') }}</router-link>
      <router-link class="v5-nav" :to="settingsLink"><Cog6ToothIcon class="i" aria-hidden="true" />{{ t('v5.shell.settings') }}</router-link>
      <router-link class="v5-nav" :to="friendCodesLink"><GiftIcon class="i" aria-hidden="true" />{{ t('v5.shell.friend_codes') }}</router-link>
      <button class="v5-nav" type="button" @click="emit('theme')">
        <component :is="theme === 'dark' ? SunIcon : MoonIcon" class="i" aria-hidden="true" />{{ theme === 'dark' ? t('v5.shell.light_mode') : t('v5.shell.dark_mode') }}
      </button>
      <hr>
      <button v-if="canImpersonate" class="v5-nav" type="button" @click="impersonate"><EyeIcon class="i" aria-hidden="true" />{{ t('v5.shell.impersonate') }}</button>
      <button v-if="impersonating" class="v5-nav" type="button" @click="stopImpersonating"><EyeSlashIcon class="i" aria-hidden="true" />{{ t('v5.shell.stop_impersonating') }}</button>
      <router-link class="v5-nav" :to="classic"><ArrowUturnLeftIcon class="i" aria-hidden="true" />{{ t('v5.shell.classic') }}</router-link>
      <button class="v5-nav v5-nav--bad" type="button" @click="emit('sign-out')"><ArrowRightStartOnRectangleIcon class="i" aria-hidden="true" />{{ t('v5.shell.sign_out') }}</button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ArrowRightStartOnRectangleIcon, ArrowUturnLeftIcon, ChevronDownIcon, Cog6ToothIcon, EyeIcon,
  EyeSlashIcon, GiftIcon, MoonIcon, StarIcon, SunIcon, UserCircleIcon, UserGroupIcon,
} from '@heroicons/vue/24/outline';
import usePopover from './usePopover';
import useV5Account from './useV5Account';

// The classic avatar menu (NavBar) with the mockup's entries: theme and the classic design.
// Matchmaking players open the profile sidebar (V5ProfileSidebar) from this button instead.
const props = defineProps({
  user: { type: Object, required: true },
  theme: { type: String, required: true },
  classic: { type: String, required: true },
  panelOpen: { type: Boolean, default: false },
});
const emit = defineEmits(['theme', 'panel', 'sign-out']);
const { t } = useI18n();
const { open, root, toggle } = usePopover();
const sidebar = computed(() => !!props.user.can_use_matchmaking);
const {
  name, avatar, impersonating, canImpersonate, profileLink, teamLink, settingsLink, friendCodesLink,
  impersonate, stopImpersonating,
} = useV5Account(props);
</script>
